import React, { useState } from 'react';
import { EYEBROW, cn } from '../lib/utils';
import { KeyRound } from 'lucide-react';

import { CredentialField } from './CredentialField';
import { Button } from './ui/button';
import { apiService } from '../services/api';
import { humanizeError } from '../lib/errors';

interface AppSecretFieldProps {
  /** Internal app id or Cognito client id — either resolves server-side. */
  appId: string;
  accessToken: string | undefined;
  /**
   * The secret if the caller already has it — the create response returns one.
   * Absent means "fetch it when the user asks".
   */
  initialSecret?: string;
  /**
   * Called once the secret has been fetched, so a page can use it — Request
   * Builder rebuilds its snippet with the real secret.
   */
  onLoaded?: (secret: string) => void;
  className?: string;
}

/**
 * The client secret, fetched on demand and copyable as often as the user likes.
 *
 * REPLACES a dashed "Shown only at creation" well that appeared verbatim in
 * three places. That copy was never true of Cognito — DescribeUserPoolClient
 * returns the secret for the life of the client — it was true only of the API,
 * which deliberately strips secrets from the bulk `GET /apps` read so a
 * `app_clients:read` holder cannot harvest them. Fetching one named secret is a
 * different question, and `GET /apps/{id}/credentials` answers it behind
 * `app_clients:write`.
 *
 * The value is never rendered in the clear: `revealable={false}` leaves copy as
 * the only way out of the field.
 */
export const AppSecretField: React.FC<AppSecretFieldProps> = ({
  appId,
  accessToken,
  initialSecret,
  onLoaded,
  className,
}) => {
  const [secret, setSecret] = useState<string>(initialSecret || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleShow = async () => {
    if (!accessToken) {
      setError('Missing access token. Please sign in again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const credentials = await apiService.getAppCredentials(appId, accessToken);
      setSecret(credentials.clientSecret);
      onLoaded?.(credentials.clientSecret);
    } catch (err: unknown) {
      console.error('Failed to load app credentials:', err);
      setError(humanizeError(err, "Couldn't load the client secret"));
    } finally {
      setIsLoading(false);
    }
  };

  if (secret) {
    return <CredentialField label="Client secret" value={secret} secret revealable={false} className={className} />;
  }

  return (
    <div className={className}>
      <p className={cn('mb-1.5', EYEBROW)}>
        Client secret
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => void handleShow()}
        loading={isLoading}
      >
        <KeyRound aria-hidden="true" />
        Show client secret
      </Button>
      {error ? (
        <p role="alert" className="mt-1.5 text-xs text-destructive-emphasis">
          {error}
        </p>
      ) : null}
    </div>
  );
};
